import React from 'react';
import Box from '@mui/material/Box';
import {LinearProgress, Paper, Typography} from '@mui/material';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import BlockIcon from '@mui/icons-material/Block';
import SearchIcon from '@mui/icons-material/Search';
import ApartmentIcon from '@mui/icons-material/Apartment';

const spinSx = {
    fontSize: 16,
    animation: 'spin 1.5s linear infinite',
    '@keyframes spin': {from: {transform: 'rotate(0deg)'}, to: {transform: 'rotate(360deg)'}},
};

/**
 * Bannière affichée pendant le traitement (groupement par adresse, calcul des colonnes).
 * @param {{ progress: number|null }} props
 */
export function ProcessingPanel({ progress }) {
    const pct = progress != null ? Math.min(100, Math.round(progress)) : null;
    return (
        <Box sx={{mt:1, mx: 2}}>
            <Paper elevation={0} sx={{
                px: 2, py: 1.5, borderRadius: 2,
                border: '1px solid', borderColor: 'divider',
                display: 'flex', flexDirection: 'column', gap: 0.8,
                width: '50%', mx: 'auto',
            }}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <HourglassEmptyIcon sx={{...spinSx, color: 'warning.main'}}/>
                    <Typography variant="body2" fontWeight="medium" color="text.secondary">
                        Traitement des données…
                    </Typography>
                    <Box sx={{ml: 'auto', display: 'flex', alignItems: 'center', gap: 1}}>
                        <Typography variant="caption" color="text.disabled">
                            Groupement · Calcul des colonnes
                        </Typography>
                        {pct != null && (
                            <Typography variant="caption" fontWeight={700} sx={{
                                fontFamily: 'monospace',
                                color: pct >= 100 ? 'success.main' : 'warning.dark',
                                bgcolor: pct >= 100 ? 'rgba(46,125,50,0.1)' : 'rgba(237,108,2,0.1)',
                                px: 0.8, py: 0.1, borderRadius: 1, minWidth: 38, textAlign: 'center',
                            }}>
                                {pct}&nbsp;%
                            </Typography>
                        )}
                    </Box>
                </Box>
                <LinearProgress
                    variant={pct != null ? 'determinate' : 'indeterminate'}
                    value={pct ?? undefined}
                    sx={{
                        borderRadius: 4, height: 5,
                        '& .MuiLinearProgress-bar': {
                            bgcolor: pct >= 100 ? 'success.main' : 'warning.main',
                            transition: 'transform 0.2s ease',
                        },
                    }}
                />
            </Paper>
        </Box>
    );
}

/**
 * Bannière affichée pendant l'initialisation du tableau (rendu DataGrid).
 * @param {{ rowsCount: number, columnsCount: number }} props
 */
export function TableLoadingPanel({rowsCount, columnsCount}) {
    return (
        <Box sx={{mt: 3, mx: 2}}>
            <Paper elevation={0} sx={{
                px: 2, py: 1.5, borderRadius: 2,
                border: '1px solid', borderColor: 'divider',
                display: 'flex', flexDirection: 'column', gap: 0.8,
                width: '50%', mx: 'auto',
            }}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <HourglassEmptyIcon sx={{...spinSx, color: 'primary.main'}}/>
                    <Typography variant="body2" fontWeight="medium" color="text.secondary">
                        Chargement du tableau…
                    </Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ml: 'auto'}}>
                        {rowsCount} ligne(s) · {columnsCount} colonnes
                    </Typography>
                </Box>
                <LinearProgress sx={{borderRadius: 4, height: 5}}/>
            </Paper>
        </Box>
    );
}

/**
 * Page de démarrage affichée quand aucune donnée n'est chargée.
 */
export function WelcomePage() {
    return (
        <Box sx={{
            mt: 8, display: 'flex', flexDirection: 'column',
            alignItems: 'center', gap: 2.5,
        }}>
            <Box sx={{
                width: 80, height: 80, borderRadius: '50%',
                bgcolor: 'rgba(25,118,210,0.08)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '2px solid rgba(25,118,210,0.18)',
            }}>
                <ApartmentIcon sx={{ fontSize: '2.5rem', color: 'primary.main', opacity: 0.7 }} />
            </Box>
            <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ mb: 0.5 }}>
                    Aucune donnée chargée
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400 }}>
                    Saisissez un ou plusieurs codes de localisation (commune ou EPCI) séparés par&nbsp;
                    <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>;</Box>
                    , puis cliquez sur <strong>Charger</strong>.
                </Typography>
            </Box>
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                px: 2, py: 1, borderRadius: 2,
                bgcolor: 'rgba(25,118,210,0.05)',
                border: '1px dashed rgba(25,118,210,0.2)',
            }}>
                <SearchIcon sx={{ fontSize: '1rem', color: 'primary.light' }} />
                <Typography variant="caption" color="text.disabled" sx={{ fontFamily: 'monospace' }}>
                    Exemple&nbsp;: 34274 &nbsp;·&nbsp; 34274;13055 &nbsp;·&nbsp; 200017469
                </Typography>
            </Box>
        </Box>
    );
}


/**
 * Bannière affichée en cas d'erreur (ex : 409 extraction déjà en cours, timeout…).
 * @param {{ error: { status: number, message: string } | null }} props
 */
export function ErrorPanel({ error }) {
    if (!error) return null;
    const isConflict = error.status === 409;
    return (
        <Box sx={{mt: 1, mx: 2}}>
            <Paper elevation={0} sx={{
                px: 2, py: 1.5, borderRadius: 2,
                border: '1px solid',
                borderColor: isConflict ? 'warning.light' : 'error.light',
                bgcolor: isConflict ? 'rgba(237,108,2,0.06)' : 'rgba(211,47,47,0.05)',
                display: 'flex', alignItems: 'center', gap: 1.5,
                width: '60%', mx: 'auto',
            }}>
                {isConflict
                    ? <BlockIcon sx={{ color: 'warning.main', flexShrink: 0 }}/>
                    : <ErrorOutlineIcon sx={{ color: 'error.main', flexShrink: 0 }}/>
                }
                <Box>
                    <Typography variant="body2" fontWeight="medium"
                        color={isConflict ? 'warning.dark' : 'error.main'}>
                        {isConflict ? 'Extraction déjà en cours' : 'Erreur de chargement'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {error.message}
                    </Typography>
                </Box>
            </Paper>
        </Box>
    );
}

